import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

export type CardProps = HTMLAttributes<HTMLDivElement> & {
  /** Use a low-elevation variant for surfaces nested inside other cards. */
  variant?: 'default' | 'subtle' | 'glass';
  /** Add interactive hover behaviour (slight scale + elevation). */
  interactive?: boolean;
};

/**
 * Card — uses tonal layering instead of borders (brand book "No-Line Rule").
 * - default: white card on bg-background; subtle ambient shadow.
 * - subtle: surface-container-low — for nested groups inside a default Card.
 * - glass: frosted, used by floating navs / modals.
 */
export const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', interactive, children, ...props }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          // `app-card` — метка для песочного вида кабинета ученика: по ней в
          // index.css карточка перекрашивается из белой в песочную. Классом, а
          // не переменной цвета: `surface-container-lowest` — это ИМЕННО белый,
          // им же рисуются белые кнопки на оранжевом, и трогать его нельзя.
          'app-card rounded-card p-6',
          variant === 'default' && 'bg-surface-container-lowest shadow-soft',
          variant === 'subtle' && 'app-card-subtle bg-surface-container-low',
          variant === 'glass' && 'glass shadow-ambient',
          interactive &&
            'cursor-pointer transition hover:shadow-ambient active:scale-[0.99]',
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  },
);

Card.displayName = 'Card';

export function CardHeader({ className, children, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('mb-4 flex items-start justify-between gap-4', className)} {...props}>
      {children}
    </div>
  );
}

export function CardTitle({ className, children, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return (
    <h3
      className={cn(
        'font-display text-xl font-bold tracking-display text-on-surface',
        className,
      )}
      {...props}
    >
      {children}
    </h3>
  );
}
